// src/components/QuickViewModal.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useWishlist } from '../context/WishlistContext';
import {
  X, Heart, ShoppingCart, Star, Check, Minus, Plus,
  ArrowRight, Award, Truck, RotateCcw, ShieldCheck
} from 'lucide-react';

const QuickViewModal = ({ product, onClose }) => {
  const { addToCart } = useCart();
  const { toggleWishlist, isInWishlist } = useWishlist();

  const [selectedImage, setSelectedImage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [added, setAdded] = useState(false);

  const images = product?.images && product.images.length > 0
    ? product.images
    : (product?.image ? [product.image] : ['https://via.placeholder.com/500']);

  const isWishlisted = isInWishlist(product?.id);

  const discountPercent = product?.originalPrice && product.originalPrice > product.price
    ? Math.round(((product.originalPrice - product.price) / product.originalPrice) * 100)
    : 0;

  const savedAmount = product?.originalPrice && product.originalPrice > product.price
    ? product.originalPrice - product.price
    : 0;

  // Lock body scroll when modal open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = 'unset'; };
  }, []);

  // Close on Escape
  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const handleAddToCart = () => {
    addToCart(product, quantity);
    setAdded(true);
    setTimeout(() => setAdded(false), 2000);
  };

  const incrementQty = () => setQuantity(prev => prev + 1);
  const decrementQty = () => setQuantity(prev => (prev > 1 ? prev - 1 : 1));

  if (!product) return null;

  const description = product.description || 'No description available.';
  const shortDescription = description.length > 150
    ? description.substring(0, 150) + '...'
    : description;

  return (
    <div
      className="fixed inset-0 z-[95] flex items-center justify-center p-4 anim-fadeIn"
      style={{ backgroundColor: 'rgba(7, 27, 54, 0.7)', backdropFilter: 'blur(6px)' }}
      onClick={onClose}
    >
      <div
        className="rounded-3xl shadow-2xl w-full max-w-4xl max-h-[92vh] overflow-hidden anim-popIn relative"
        style={{ backgroundColor: 'var(--color-card-bg)' }}
        onClick={(e) => e.stopPropagation()}
      >

        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-20 w-10 h-10 rounded-full flex items-center justify-center transition hover:scale-110 shadow-lg"
          style={{ backgroundColor: '#FFFFFF', color: '#9B3F5B' }}
        >
          <X size={20} />
        </button>

        <div className="grid grid-cols-1 md:grid-cols-2 max-h-[92vh] overflow-y-auto">

          {/* LEFT: Images */}
          <div className="relative p-4 md:p-6" style={{ backgroundColor: '#FFF2F6' }}>
            <div className="relative rounded-2xl overflow-hidden shadow-lg"
              style={{ border: `2px solid #F5AEC0` }}>
              <img
                src={images[selectedImage]}
                alt={product.title}
                className="w-full h-72 md:h-96 object-cover"
              />

              {/* Discount Badge */}
              {discountPercent > 0 && (
                <div
                  className="absolute top-3 left-3 text-white text-sm font-bold px-3 py-1.5 rounded-full anim-pulseGlow"
                  style={{ backgroundColor: '#DC829A' }}
                >
                  -{discountPercent}% OFF
                </div>
              )}

              {/* Featured Badge */}
              {product.featured && (
                <div
                  className="absolute top-3 right-3 text-white text-xs font-bold px-3 py-1.5 rounded-full flex items-center gap-1 shadow-lg"
                  style={{ backgroundColor: '#F5AEC0' }}
                >
                  <Award size={12} /> Featured
                </div>
              )}

              {/* Wishlist */}
              <button
                onClick={() => toggleWishlist(product)}
                className="absolute bottom-3 right-3 rounded-full p-3 shadow-lg transition hover:scale-110"
                style={{ backgroundColor: '#FFFFFF' }}
              >
                <Heart
                  size={20}
                  className={isWishlisted ? 'fill-pink-500 text-pink-500 anim-heartBeat' : ''}
                  style={!isWishlisted ? { color: '#DC829A' } : {}}
                />
              </button>
            </div>

            {/* Thumbnails */}
            {images.length > 1 && (
              <div className="flex gap-2 mt-3 overflow-x-auto pb-1">
                {images.map((img, i) => (
                  <button
                    key={i}
                    onClick={() => setSelectedImage(i)}
                    className="shrink-0 w-16 h-16 rounded-lg overflow-hidden transition-all"
                    style={{
                      border: selectedImage === i ? `2px solid #DC829A` : `2px solid #F5AEC0`,
                      opacity: selectedImage === i ? 1 : 0.6,
                    }}
                  >
                    <img src={img} alt={`Thumb ${i + 1}`} className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* RIGHT: Details */}
          <div className="p-5 md:p-6 flex flex-col">

            {/* Category */}
            <p className="text-xs font-bold uppercase mb-1" style={{ color: '#DC829A' }}>
              {product.category}
            </p>

            {/* Title */}
            <h2 className="text-xl md:text-2xl font-bold mb-2 leading-tight" style={{ color: '#9B3F5B' }}>
              {product.title}
            </h2>

            {/* Rating */}
            <div className="flex items-center gap-2 mb-3">
              <div className="flex">
                {[...Array(5)].map((_, i) => (
                  <Star
                    key={i}
                    size={14}
                    className={i < Math.floor(product.rating || 5)
                      ? 'fill-pink-400 text-pink-400'
                      : 'text-gray-300'}
                  />
                ))}
              </div>
              <span className="text-xs" style={{ color: '#DC829A' }}>
                ({product.rating || 5.0}) • {product.reviews || 0} reviews
              </span>
            </div>

            {/* Price */}
            <div className="flex items-baseline gap-2 mb-4 flex-wrap">
              <span className="text-2xl md:text-3xl font-bold" style={{ color: '#DC829A' }}>
                Rs. {product.price}
              </span>
              {product.originalPrice && product.originalPrice > product.price && (
                <>
                  <span className="text-sm line-through" style={{ color: '#F5AEC0' }}>
                    Rs. {product.originalPrice}
                  </span>
                  <span className="text-xs font-bold px-2 py-1 rounded-full text-white"
                    style={{ backgroundColor: '#DC829A' }}>
                    Save Rs. {savedAmount}
                  </span>
                </>
              )}
            </div>

            {/* Stock */}
            {product.inventory !== undefined && (
              <div className="flex items-center gap-2 mb-3 text-xs">
                <div className={`w-2 h-2 rounded-full ${product.inventory > 0 ? 'bg-pink-500' : 'bg-red-500'}`}></div>
                <span style={{ color: product.inventory > 0 ? '#DC829A' : '#EF4444' }}>
                  {product.inventory > 0 ? `In Stock (${product.inventory})` : 'Out of Stock'}
                </span>
              </div>
            )}

            {/* Description */}
            <p className="text-sm leading-relaxed mb-4" style={{ color: '#9B3F5B', opacity: 0.85 }}>
              {shortDescription}
            </p>

            {/* Quantity */}
            <div className="flex items-center gap-3 mb-4">
              <span className="text-sm font-semibold" style={{ color: '#9B3F5B' }}>Qty:</span>
              <div className="flex items-center rounded-full"
                style={{ border: `1px solid #F5AEC0`, backgroundColor: '#FFF2F6' }}>
                <button onClick={decrementQty}
                  className="p-2 rounded-l-full transition hover:bg-pink-100"
                  style={{ color: '#9B3F5B' }}>
                  <Minus size={14} />
                </button>
                <span className="px-3 font-bold text-sm min-w-[30px] text-center" style={{ color: '#9B3F5B' }}>
                  {quantity}
                </span>
                <button onClick={incrementQty}
                  className="p-2 rounded-r-full transition hover:bg-pink-100"
                  style={{ color: '#9B3F5B' }}>
                  <Plus size={14} />
                </button>
              </div>
              <span className="text-sm ml-auto" style={{ color: '#DC829A' }}>
                Total: <strong>Rs. {product.price * quantity}</strong>
              </span>
            </div>

            {/* Add to Cart */}
            <button
              onClick={handleAddToCart}
              className="w-full rounded-full py-3.5 font-bold flex items-center justify-center gap-2 btn-2d shadow-lg mb-2 transition-all"
              style={{
                backgroundColor: added ? '#DC829A' : '#F5AEC0',
                color: '#FFFFFF',
              }}
            >
              {added ? (
                <><Check size={18} className="anim-popIn" /> Added to Cart!</>
              ) : (
                <><ShoppingCart size={18} /> Add to Cart</>
              )}
            </button>

            {/* View Full Details */}
            <Link
              to={`/product/${product.id}`}
              onClick={onClose}
              className="w-full rounded-full py-3 font-semibold text-sm flex items-center justify-center gap-2 transition border-2"
              style={{
                borderColor: '#F5AEC0',
                color: '#DC829A',
                backgroundColor: 'transparent',
              }}
            >
              View Full Details <ArrowRight size={14} />
            </Link>

            {/* Trust Badges */}
            <div className="grid grid-cols-3 gap-2 mt-4 pt-4 border-t" style={{ borderColor: '#F5AEC0' }}>
              <div className="flex flex-col items-center text-center">
                <Truck size={18} style={{ color: '#DC829A' }} />
                <span className="text-[10px] mt-1 font-semibold" style={{ color: '#9B3F5B' }}>Free Ship</span>
              </div>
              <div className="flex flex-col items-center text-center">
                <RotateCcw size={18} style={{ color: '#DC829A' }} />
                <span className="text-[10px] mt-1 font-semibold" style={{ color: '#9B3F5B' }}>7-Day Return</span>
              </div>
              <div className="flex flex-col items-center text-center">
                <ShieldCheck size={18} style={{ color: '#DC829A' }} />
                <span className="text-[10px] mt-1 font-semibold" style={{ color: '#9B3F5B' }}>100% Organic</span>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default QuickViewModal;